import { sumAccommodationViewsTotal } from './sumAccommodationViewTotal'
import { FeeItemsBreakdown } from 'businessTraveller/components/checkout/trip-request-review/BusinessTravellerBudgetComparisonBreakdown'
import { FlightSearchBudgets } from 'lib/flights/flightUtils'
export interface AccommodationBudgetInfo {
  totalCost: number
  calculatedBudget: number
  budgetPerNight: number
  duration: number
  roomCount: number
  isOutOfBudget: boolean
}

interface CalculateFlightBudgetParams {
  flightViews: Array<App.Checkout.FlightItemView>
  flightSearchBudgets: Array<FlightSearchBudgets>
  flightBudget: number
}

export interface FlightBudgetInfo {
  /** Total cost of all flights (excluding other fees) */
  flightTotal: number
  /** Total cost of flights that have a budget set (excluding other fees) */
  budgetedFlightTotal: number
  /** Total other fees (seat selection + baggage) */
  flightTotalOtherFees: number
  /** Breakdown of other fees by category */
  feeItemsBreakdown: Array<FeeItemsBreakdown>
  /** Total budgeted flights + other fees */
  budgetedFlightTotalWithOtherFees: number
  /** Total flights + other fees */
  flightTotalWithOtherFees: number
  /** Whether the booking exceeds the budget */
  isOutOfBudget: boolean
}

/**
 * Build the budget breakdown string for display.
 * Example: "$150 × 1 night × 3 rooms"
 */
export function formatAccommodationBudgetBreakdown(
  formattedBudgetPerNight: string,
  duration: number,
  roomCount: number,
): string {
  const nightLabel = duration === 1 ? 'night' : 'nights'
  const roomLabel = roomCount === 1 ? 'room' : 'rooms'
  return `${formattedBudgetPerNight} × ${duration} ${nightLabel} × ${roomCount} ${roomLabel}`
}

/**
 * Calculate accommodation budget information for trip request display.
 * Budget formula: budgetPerNight × duration × roomCount
 */
export function calculateAccommodationBudget(params: {
  accommodationItems: Array<App.Checkout.AccommodationItem>
  accommodationViews: Array<App.Checkout.AccommodationOfferView>
  accommodationBudget: App.BusinessTraveller.BusinessBudget | null
}): AccommodationBudgetInfo {
  const { accommodationItems, accommodationViews, accommodationBudget } = params

  const totalCost = sumAccommodationViewsTotal(accommodationViews)
  const roomCount = accommodationItems.length
  const duration = roomCount > 0 && 'duration' in accommodationItems[0]
    ? accommodationItems[0].duration
    : 0
  const budgetPerNight = accommodationBudget?.budget ?? 0
  const calculatedBudget = budgetPerNight * duration * roomCount
  const isOutOfBudget = calculatedBudget > 0 && totalCost > calculatedBudget

  return {
    totalCost,
    calculatedBudget,
    budgetPerNight,
    duration,
    roomCount,
    isOutOfBudget,
  }
}

/**
 * Calculate flight budget information for trip request display.
 * Includes flight costs, other fees (seat selection, baggage), and budget comparison.
 */
export function calculateFlightBudget(params: CalculateFlightBudgetParams): FlightBudgetInfo {
  const { flightViews, flightSearchBudgets, flightBudget } = params

  // Only count flights that have a budget set
  const budgetedFlightTotal = flightViews
    .flatMap(flightView => flightView.flights)
    .reduce((acc, flight, index) => {
      const cost = flight.cost ?? 0
      const hasBudget = flightSearchBudgets?.[index]?.matchedBudget
      return acc + cost * (hasBudget ? 1 : 0)
    }, 0)

  // Total flights cost (excluding other fees)
  const flightTotal = flightViews
    .flatMap(flightView => flightView.flights)
    .reduce((acc, flight) => acc + (flight.cost ?? 0), 0)

  // Calculate other fees (seat selection, baggage)
  const otherFeeTotals = {
    seatSelection: 0,
    baggage: 0,
  }

  flightViews.forEach(flightView => {
    const otherFees = flightView.totals.otherFees
    if (otherFees) {
      otherFeeTotals.seatSelection += otherFees.flightSelectedSeatsTotal ?? 0
      otherFeeTotals.baggage += otherFees.flightBaggageTotal ?? 0
    }
  })

  const flightTotalOtherFees = Object.values(otherFeeTotals).reduce((acc, value) => acc + value, 0)

  const feeItemsBreakdown: Array<FeeItemsBreakdown> = [
    {
      label: 'Seat selection',
      value: otherFeeTotals.seatSelection,
    },
    {
      label: 'Baggage',
      value: otherFeeTotals.baggage,
    },
  ]

  // Total budgeted flights + other fees
  const budgetedFlightTotalWithOtherFees = budgetedFlightTotal + flightTotalOtherFees
  // Total flights + other fees
  const flightTotalWithOtherFees = flightTotal + flightTotalOtherFees

  const isOutOfBudget = flightBudget > 0 && budgetedFlightTotalWithOtherFees > flightBudget

  return {
    flightTotal,
    budgetedFlightTotal,
    flightTotalOtherFees,
    feeItemsBreakdown,
    budgetedFlightTotalWithOtherFees,
    flightTotalWithOtherFees,
    isOutOfBudget,
  }
}

/**
 * Extract flight route information from flight views for budget lookup.
 * Returns departure and arrival airport codes for each journey, ignoring stopovers.
 */
export function extractFlightRoutes(flightViews: Array<App.Checkout.FlightItemView>): Array<{
  departureAirportCode: string
  arrivalAirportCode: string
}> {
  return flightViews.flatMap(flightView =>
    flightView.flights.map(flight => ({
      departureAirportCode: flight.journeyFlight.flights[0].departingAirport,
      arrivalAirportCode: flight.journeyFlight.flights[flight.journeyFlight.flights.length - 1].arrivalAirport,
    })),
  )
}
